/**
 * Poseidon dashboard — runtime configuration.
 *
 * Everything here is browser-safe. There are deliberately no API keys, MT5
 * credentials, or bridge tokens: §11 keeps those in the Rust daemon's secret
 * store, and nothing that reaches this bundle may contain them.
 */
import type { DataSourceMode, FreshnessThresholds } from './api/types';

/**
 * Data source mode.
 *
 * `mock` is the default *only* because the Rust API does not exist yet. It is
 * not a fallback: when live mode fails, the UI shows an error state. It never
 * quietly substitutes synthetic data for a failed real request.
 */
export const API_MODE: DataSourceMode = import.meta.env.VITE_API_MODE === 'live' ? 'live' : 'mock';

export const IS_MOCK_MODE = API_MODE === 'mock';

/** Same-origin by default: in production the daemon serves both UI and API. */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * WebSocket URL. Derived from the page origin so the daemon can serve the UI
 * from any localhost port; `/ws` is proxied to 127.0.0.1:8000 in dev.
 */
export function resolveWebSocketUrl(): string {
  const override = import.meta.env.VITE_WS_URL;
  if (override && override.length > 0) return override;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

/**
 * Freshness thresholds.
 *
 * §7.1 uses a five-second quote-staleness gate as the *starting* condition, so
 * five seconds is the stale-tick default here. These are display thresholds
 * only; the daemon's risk engine remains the authority on whether a quote is
 * too old to trade on.
 */
export const FRESHNESS_THRESHOLDS: FreshnessThresholds = {
  tick_aging_ms: 2_000,
  tick_stale_ms: 5_000,
  bar_aging_ms: 90_000,
  bar_stale_ms: 180_000,
  snapshot_aging_ms: 15_000,
  snapshot_stale_ms: 60_000,
  clock_drift_warn_ms: 500,
  clock_drift_fail_ms: 2_000,
};

/** Polling cadence for REST snapshots. The WebSocket carries live deltas. */
export const POLL_INTERVALS = {
  ribbon: 5_000,
  kpis: 10_000,
  positions: 5_000,
  orders: 5_000,
  signals: 5_000,
  events: 15_000,
  riskLimits: 15_000,
  readiness: 30_000,
  operations: 15_000,
  quotes: 3_000,
} as const;

/** WebSocket reconnect policy: exponential backoff with jitter, capped. */
export const SOCKET_POLICY = {
  baseBackoffMs: 500,
  maxBackoffMs: 15_000,
  /** Force a reconnect when no frame arrives within this window. */
  heartbeatTimeoutMs: 20_000,
  maxUnknownTypeReports: 5,
} as const;

/**
 * Confirmation phrases (§3.1).
 *
 * These strings are shared with the daemon contract: the server must verify the
 * phrase itself rather than trusting that a dialog was shown. Keeping them in
 * one table means the UI, the tests and the API docs cannot drift apart.
 */
export const CONFIRMATION_PHRASES = {
  set_execution_mode: 'CHANGE EXECUTION MODE',
  approve_order_intent: 'APPROVE ORDER',
  reject_order_intent: 'REJECT ORDER',
  cancel_order: 'CANCEL ORDER',
  close_position: 'CLOSE POSITION',
  soft_halt: 'SOFT HALT',
  hard_halt: 'HARD HALT',
  release_halt: 'RELEASE HALT',
  set_risk_limit: 'CHANGE RISK LIMIT',
  set_allow_list: 'CHANGE ALLOW LIST',
} as const;

export type ConfirmationPhraseKey = keyof typeof CONFIRMATION_PHRASES;

/** Build identifier shown on the Operations page. */
export const BUILD_ID = import.meta.env.VITE_BUILD_ID ?? 'dev-local';
